import { useMemo } from "react";
import { GoalCategory } from "web_myplann/Goals/Types/GoalTypes";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type GoalCategoryStrings = {
	defaultName: string;
	amountLabel: string;
	amountHint: string | undefined;
	ownFundsLabel: string;
	loanLabel: string;
	loanHint: string | undefined;
	remainingLabel: string;
	rentCalcAvailable: boolean;
	rentCalcDefaultOn: boolean;
	getDefaultName: (category: GoalCategory | undefined) => string;
};

const RENT_CALC_AVAILABLE_CATEGORIES: ReadonlyArray<GoalCategory | undefined> =
	[undefined, GoalCategory.Retirement, GoalCategory.Custom];

const RENT_CALC_DEFAULT_ON_CATEGORIES: ReadonlyArray<GoalCategory> = [
	GoalCategory.Retirement,
];

export const useGoalCategoryStrings = (
	category: GoalCategory | undefined,
): GoalCategoryStrings => {
	const { t } = useResource();

	const defaultNameByCategory = useMemo(
		(): Record<GoalCategory, string> => ({
			[GoalCategory.Housing]: t(Resources.Goals.Form.DefaultNames.Housing),
			[GoalCategory.Car]: t(Resources.Goals.Form.DefaultNames.Car),
			[GoalCategory.Retirement]: t(
				Resources.Goals.Form.DefaultNames.Retirement,
			),
			[GoalCategory.Education]: t(Resources.Goals.Form.DefaultNames.Education),
			[GoalCategory.Vacation]: t(Resources.Goals.Form.DefaultNames.Vacation),
			[GoalCategory.Custom]: "",
		}),
		[t],
	);

	const amountLabelByCategory = useMemo(
		(): Partial<Record<GoalCategory, string>> => ({
			[GoalCategory.Housing]: t(Resources.Goals.Form.AmountLabels.Housing),
			[GoalCategory.Car]: t(Resources.Goals.Form.AmountLabels.Car),
			[GoalCategory.Retirement]: t(
				Resources.Goals.Form.AmountLabels.Retirement,
			),
			[GoalCategory.Education]: t(Resources.Goals.Form.AmountLabels.Education),
			[GoalCategory.Vacation]: t(Resources.Goals.Form.AmountLabels.Vacation),
			[GoalCategory.Custom]: t(Resources.Goals.Form.AmountLabels.Default),
		}),
		[t],
	);

	const amountHintByCategory = useMemo(
		(): Partial<Record<GoalCategory, string>> => ({
			[GoalCategory.Retirement]: t(Resources.Goals.Form.AmountHints.Retirement),
			[GoalCategory.Education]: t(Resources.Goals.Form.AmountHints.Education),
			[GoalCategory.Vacation]: t(Resources.Goals.Form.AmountHints.Vacation),
		}),
		[t],
	);

	const ownFundsLabelByCategory = useMemo(
		(): Partial<Record<GoalCategory, string>> => ({
			[GoalCategory.Housing]: t(Resources.Goals.Form.OwnFundsLabels.Housing),
			[GoalCategory.Car]: t(Resources.Goals.Form.OwnFundsLabels.Car),
			[GoalCategory.Retirement]: t(
				Resources.Goals.Form.OwnFundsLabels.Retirement,
			),
			[GoalCategory.Education]: t(
				Resources.Goals.Form.OwnFundsLabels.Education,
			),
			[GoalCategory.Vacation]: t(Resources.Goals.Form.OwnFundsLabels.Vacation),
			[GoalCategory.Custom]: t(Resources.Goals.Form.OwnFundsLabels.Default),
		}),
		[t],
	);

	const loanHintByCategory = useMemo(
		(): Partial<Record<GoalCategory, string>> => ({
			[GoalCategory.Housing]: t(Resources.Goals.Form.LoanHints.Housing),
			[GoalCategory.Car]: t(Resources.Goals.Form.LoanHints.Car),
		}),
		[t],
	);

	const getDefaultName = (cat: GoalCategory | undefined) =>
		cat ? (defaultNameByCategory[cat] ?? "") : "";

	const usesDefaultRemainingLabel =
		!category || category === GoalCategory.Custom;

	const defaultName = getDefaultName(category);
	const amountLabel = category
		? (amountLabelByCategory[category] ?? "")
		: t(Resources.Goals.Form.AmountLabels.Default);
	const amountHint = category ? amountHintByCategory[category] : undefined;
	const ownFundsLabel = category
		? (ownFundsLabelByCategory[category] ?? "")
		: t(Resources.Goals.Form.OwnFundsLabels.Default);
	const loanLabel = t(Resources.Goals.Form.loanLabel);
	const loanHint = category ? loanHintByCategory[category] : undefined;
	const remainingLabel = usesDefaultRemainingLabel
		? t(Resources.Goals.Form.RemainingLabels.Default)
		: t(Resources.Goals.Form.RemainingLabels.Saving);
	const rentCalcAvailable = RENT_CALC_AVAILABLE_CATEGORIES.includes(category);
	const rentCalcDefaultOn = category
		? RENT_CALC_DEFAULT_ON_CATEGORIES.includes(category)
		: false;

	return {
		defaultName,
		amountLabel,
		amountHint,
		ownFundsLabel,
		loanLabel,
		loanHint,
		remainingLabel,
		rentCalcAvailable,
		rentCalcDefaultOn,
		getDefaultName,
	};
};
